import { TServerSegment } from '@/shared/types/Editor';
import { MediaType, Project, SharedProject } from '@/shared/types/Project';
import { Language } from '@/shared/types/Settings';
import { ProjectVideoFeedback } from '@/shared/types/VideoFeedback';
import { Voice } from '@/shared/types/Voice';

import { BASE_URL } from '../constants';
import { del, get, getPublic, patch, post, put } from '../requestMethods';
import { adaptProject } from './adapters/adaptProject';
import {
  AddSegmentRequestData,
  AddSegmentsResponse,
  ApplyGlossaryToProjectRequestData,
  BurningSubtitles,
  CopyProjectRequestData,
  CreateProjectRequestData,
  CreateTranscriptRequestData,
  DeleteSegmentResponse,
  GetDemoCoversResponse,
  GetSharedProjectParams,
  MergeSegmentsRequestData,
  RunCheckFaceTaskResponse,
  RunLipSyncPipelineResponse,
  RunLipSyncTask,
  RunLipSyncTaskResponse,
  ShareProjectResponse,
  TranscriptVersionDetail,
  TranscriptVersionView,
  UpdateProjectPromptRequestData,
  UpdateProjectRequestData,
  UpdateSegmentsRequestData,
  UpdateSegmentsResponse,
} from './types';

const URL = `${BASE_URL}/api/project`;

export const PROJECT_API = {
  // apply glossary to project
  applyGlossaryToProject: ({ glossaryId, projectId }: ApplyGlossaryToProjectRequestData) =>
    patch(`${URL}/${projectId}/glossary${glossaryId ? `?glossary_id=${glossaryId}` : ''}`),

  // Burn subtitles
  burnSubtitles: ({ id, videoType = 'translated' }: BurningSubtitles) =>
    post<{ ok: boolean }>(`${URL}/${id}/burn_subtitles?video_type=${videoType}`),

  // Copy project (used for translating project to another language)
  copyProject: ({ id, dst_lang, free_dubbing }: CopyProjectRequestData) =>
    post<Project>(`${URL}/${id}/copy`, { adapter: adaptProject, data: { dst_lang, free_dubbing } }),

  // Dub video
  generateVideo: (id: Project['id']) => post<Project>(`${URL}/${id}/generate`, { adapter: adaptProject }),

  // Hide subtitles tooltip
  hideSubtitlesTooltip: ({ id, videoType = 'translated' }: BurningSubtitles) =>
    post<Project>(`${URL}/${id}/set_subtitles_banner_displayed?video_type=${videoType}`, {
      adapter: adaptProject,
    }),

  mergeSegments: (projectId: Project['id'], data: MergeSegmentsRequestData) =>
    post<UpdateSegmentsResponse>(`${URL}/${projectId}/transcript/merge`, { data }),

  // Leave feedback for dubbed video
  postFeedback: ({ id, ...rest }: ProjectVideoFeedback) =>
    post<{
      ok: boolean;
    }>(`${URL}/${id}/feedback`, { data: rest }),

  previewVoiceover: (id: Project['id'], data: { segment_ids: string[] }) =>
    post<{ task_id: string }>(`${URL}/${id}/preview_voiceover`, { data }),

  rollbackTranscriptVersion: (id: Project['id'], version: number) =>
    post<TranscriptVersionView>(`${URL}/${id}/transcript/versions/${version}/rollback`),

  runCheckFaceTask: (projectId: Project['id']) =>
    post<RunCheckFaceTaskResponse>(`${URL}/${projectId}/run_check_face_task`),

  runLipSyncPipeline: ({ isFreeLipSync, isMultiSpeakers, lipsyncModel, projectId }: RunLipSyncTask) =>
    post<RunLipSyncPipelineResponse>(`${URL}/${projectId}/lipsync-pipeline`, {
      data: {
        is_free_lipsync: isFreeLipSync,
        is_multiple_speakers: isMultiSpeakers,
        ...(lipsyncModel && { lipsync_model: lipsyncModel }),
      },
    }),

  runLipSyncTask: ({ isFreeLipSync, isMultiSpeakers, lipsyncModel, projectId }: RunLipSyncTask) =>
    post<RunLipSyncTaskResponse>(`${URL}/${projectId}/run_lipsync_task`, {
      data: {
        is_free_lipsync: isFreeLipSync,
        is_multiple_speakers: isMultiSpeakers,
        ...(lipsyncModel && { lipsync_model: lipsyncModel }),
      },
    }),

  // Send video download stats
  sendMediaDownloadStats: (id: Project['id'], mediaType: MediaType) =>
    post(`${URL}/${id}/media_download`, { data: { media_type: mediaType } }),

  // Share project
  shareProject: (id: Project['id']) => post<ShareProjectResponse>(`${URL}/${id}/share`),

  warmupVoiceover: (id: Project['id']) => post<{ task_id: string }>(`${URL}/${id}/preview_voiceover/warmup`),

  // Editor requests
  addSegments: (projectId: Project['id'], payload: AddSegmentRequestData[]) =>
    post<AddSegmentsResponse>(`${URL}/${projectId}/transcript`, { data: { segments: payload } }),

  createDemoProject: (dstLang: Language['value']) => post<Project>(`${URL}/demo`, { data: { dst_lang: dstLang } }),

  // Create project
  createProject: (data: CreateProjectRequestData) => post<Project>(`${URL}/`, { adapter: adaptProject, data }),

  // Create transcript by uploading SRT
  createTranscript: (data?: CreateTranscriptRequestData) =>
    post<{
      transcript_id: Project['transcript_id'];
    }>(`${URL}/create_transcript`, { data }),

  createTranscriptVersion: (id: Project['id'], data: { label?: string }) =>
    post<TranscriptVersionView>(`${URL}/${id}/transcript/versions`, { data }),

  createTranscriptVersionAutoSave: (id: Project['id']) =>
    post<TranscriptVersionView>(`${URL}/${id}/transcript/versions/auto`),

  deleteSegment: (projectId: Project['id'], segmentId: TServerSegment['id']) =>
    del<DeleteSegmentResponse>(`${URL}/${projectId}/transcript/${segmentId}`),

  getDemoCovers: () => get<GetDemoCoversResponse>(`${URL}/demo/covers`),

  // Get a project by id
  getProjectById: (id: Project['id']) => get<Project>(`${URL}/${id}`, { adapter: adaptProject }),

  getSegments: (id: Project['id'], segmentsIds?: TServerSegment['id'][]) =>
    get<{
      segments: TServerSegment[];
    }>(`${URL}/${id}/transcript${segmentsIds ? `?segments=${segmentsIds.join(',')}` : ''}`),

  getSharedProject: ({ sharing_id }: GetSharedProjectParams) =>
    getPublic<SharedProject>(`${URL}/shared/${sharing_id}`, { params: { sharing_id } }),

  getTranscriptVersionDetail: (id: Project['id'], version: number) =>
    get<TranscriptVersionDetail>(`${URL}/${id}/transcript/versions/${version}`),

  getTranscriptVersions: (id: Project['id']) =>
    get<TranscriptVersionView[]>(`${URL}/${id}/transcript/versions`),

  // Get available voices
  getVoicePool: (id: Project['id'], spaceId: string) => get<Voice[]>(`${URL}/${id}/voice_pool?space_id=${spaceId}`),

  // Update or delete project
  updateProject: ({ id, ...rest }: UpdateProjectRequestData) =>
    patch<Project>(`${URL}/${id}`, { adapter: adaptProject, data: rest }),

  updateProjectPrompt: ({ id, prompt }: UpdateProjectPromptRequestData) =>
    put<Project>(`${URL}/${id}/prompt`, { data: { prompt: prompt || null } }),

  updateSegments: (projectId: Project['id'], data: UpdateSegmentsRequestData[]) =>
    patch<UpdateSegmentsResponse>(`${URL}/${projectId}/transcript`, { data: { segments: data } }),
};
